import { useRef, useState } from 'react';
import api from '../../utils/api';
import { useHtmlDir } from '../organic/motion';
import OrganicLoader from '../organic/OrganicLoader.jsx';
import DangerZone from '../DangerZone';
import LabNotesTab from './tabs/LabNotesTab';
import DnaTab from './tabs/DnaTab';
import TaxonomyTab from './tabs/TaxonomyTab';
import PhotosTab from './tabs/PhotosTab';
import BankTab from './tabs/BankTab';
import MicroscopyTab from './tabs/MicroscopyTab';
import AnalyticsTab from './tabs/AnalyticsTab';
import PipelineTab from './tabs/PipelineTab';
import LabCodeEditor from '../specimens/LabCodeEditor';
import { can } from '../../utils/permissions';
import { mapTarget } from './quickActions';
import * as S from '../specimens/styles';

/*
 * An expanded hub row: the isolate's dossier (read once, cached by
 * useDossiers), in its tabs. Each tab's buttons open the same quick-action
 * drawer the row's own buttons do, aimed at that plate, slant or extract.
 * It opens on the tab for where the isolate is: a banked one on its bank
 * location, anything else on its lab notes. Its gallery is the row's
 * photograph; here are only its own page and the map.
 *
 * Whoever reads the culture pipeline's tasks (pipeline:read) also gets its
 * Pipeline tab (tabs/PipelineTab.js): each stage's task, who has it, and the
 * handoff history -- where the pipeline's emails link (?tab=pipeline).
 *
 * Analytics (tabs/AnalyticsTab.js, plan phase 9): the growth curves, tray
 * readings marked; the spore analyses and the way to the workbench; and,
 * from phase 10, the thermal ROI cards. Its panel may switch tabs (onTab).
 *
 * The tabs follow the WAI-ARIA tabs pattern: arrow keys move between them
 * in the reading direction (so Left is "next" right to left), Home and End
 * go to the ends, and only the selected tab is in the Tab order.
 *
 * At its foot, apart from everything else (DangerZone), **Archive isolate**,
 * when the dossier says this caller may (`actions.archive`,
 * routes/ownership.py: a system admin, a collection manager, or whoever
 * registered it while the lab has done nothing with it beyond its waiting
 * plates). It asks for a reason; archived, the isolate leaves the hub's
 * lists (`onArchived`: the hub reads its rows again).
 */
export const TABS = ['lab', 'dna', 'taxonomy', 'photos', 'microscopy', 'analytics', 'bank', 'pipeline'];
const PANELS = { lab: LabNotesTab, pipeline: PipelineTab, dna: DnaTab, taxonomy: TaxonomyTab, photos: PhotosTab,
  microscopy: MicroscopyTab, analytics: AnalyticsTab, bank: BankTab };

/** The tabs this person sees: Pipeline only with pipeline:read. */
export function tabsFor(user) {
  return TABS.filter((key) => key !== 'pipeline' || can(user, 'pipeline:read'));
}

export default function IsolateDetails({
  item, entry, onRetry, tab, onTab, user, t, onAction, onNavigate, onStorageSaved, onRefresh, onArchived,
}) {
  const rtl = useHtmlDir() === 'rtl';
  const tabs = tabsFor(user);
  const current = tabs.includes(tab) ? tab : (item.population === 'bank' ? 'bank' : 'lab');
  const refs = useRef({});
  const base = `hub-${item.specimen_id}`;

  const move = (e) => {
    const i = tabs.indexOf(current);
    const forward = rtl ? 'ArrowLeft' : 'ArrowRight';
    const back = rtl ? 'ArrowRight' : 'ArrowLeft';
    let next = null;
    if (e.key === forward) next = tabs[(i + 1) % tabs.length];
    else if (e.key === back) next = tabs[(i - 1 + tabs.length) % tabs.length];
    else if (e.key === 'Home') next = tabs[0];
    else if (e.key === 'End') next = tabs[tabs.length - 1];
    if (!next) return;
    e.preventDefault();
    onTab(next);
    refs.current[next]?.focus();
  };

  const dossier = entry?.data;
  const map = mapTarget(item, dossier?.specimen);
  const Panel = PANELS[current];

  return (
    <div className="hub-details" data-testid={`hub-details-${item.specimen_id}`}>
      <div className="hub-details__links">
        {!item.redacted && (
          <button type="button" style={S.ghostBtn} onClick={() => onNavigate(`/specimens/${item.specimen_id}`)}
            title={t('tip.action.open')} data-testid="hub-details-open">{t('details.openPage')}</button>
        )}
        {map && (
          <button type="button" style={S.ghostBtn} onClick={() => onNavigate(map.path, map.state)}
            title={t('tip.showOnMap')} data-testid="hub-details-map">{t('details.showOnMap')}</button>
        )}
        {/* The bank's own code, set by hand with a reason (0083). */}
        {!item.redacted && item.status === 'active' && can(user, 'specimen:write') && (
          <LabCodeEditor compact specimenId={item.specimen_id} labCode={item.lab_code} canEdit
            onSaved={() => onRefresh?.(item)} />
        )}
      </div>

      <div role="tablist" aria-label={t('details.tabs')} className="hub-tabs" onKeyDown={move}>
        {tabs.map((key) => (
          <button key={key} type="button" role="tab" id={`${base}-tab-${key}`}
            aria-selected={current === key} aria-controls={`${base}-panel-${key}`}
            tabIndex={current === key ? 0 : -1} ref={(el) => { refs.current[key] = el; }}
            onClick={() => onTab(key)} title={t(`tip.tab.${key}`)} data-testid={`hub-tab-${key}`}
            className="hub-tab">
            {t(`tabs.${key}`)}
          </button>
        ))}
      </div>

      <div role="tabpanel" id={`${base}-panel-${current}`} aria-labelledby={`${base}-tab-${current}`}
        className="hub-tabpanel" tabIndex={0}>
        {entry?.loading && !dossier && (
          <OrganicLoader block label={t('common:loading')} slowLabel={t('common:stillWorking')} />
        )}
        {entry?.error && !dossier && (
          <div role="alert" style={S.error}>
            {entry.error}{' '}
            <button type="button" style={S.ghostBtn} onClick={onRetry} title={t('tip.retry')}>{t('retry')}</button>
          </div>
        )}
        {dossier && (
          <>
            {dossier.restricted && <p role="note" style={{ ...S.hint, marginTop: 0 }}>{t('details.restricted')}</p>}
            {dossier.truncated && <p role="note" style={{ ...S.hint, marginTop: 0 }}>{t('details.truncated')}</p>}
            <Panel item={item} dossier={dossier} user={user} t={t} onAction={onAction}
              onNavigate={onNavigate} onStorageSaved={onStorageSaved} onRefresh={onRefresh}
              onTab={(key) => { onTab(key); refs.current[key]?.focus(); }} />
          </>
        )}
      </div>

      {dossier?.actions?.archive && <ArchiveIsolate item={item} t={t} onArchived={onArchived} />}
    </div>
  );
}

/* Archive isolate, with the reason the specimen's history records
 * (POST /specimens/{id}/archive {reason}: 3 characters or more). */
function ArchiveIsolate({ item, t, onArchived }) {
  const [reason, setReason] = useState('');
  const said = reason.trim();
  const archive = () => api.post(`/specimens/${item.specimen_id}/archive`, { reason: said })
    .then(() => onArchived?.(item));
  return (
    <div style={{ paddingInline: 12, paddingBlockEnd: 12 }}>
      <DangerZone testId="hub-archive" label={t('archive.button')} title={t('archive.tip')}
        confirmTitle={t('archive.title', { code: item.specimen_code })}
        consequences={[t('archive.consequence1'), t('archive.consequence2')]}
        confirmLabel={t('archive.confirm')} confirmingLabel={t('archive.archiving')}
        failedText={t('archive.failed')} ready={said.length >= 3} onConfirm={archive}
        onOpenChange={(open) => { if (open) setReason(''); }}>
        <label style={{ ...S.label, marginBlockEnd: 16 }}>
          {t('archive.reason')}
          <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} style={S.input}
            title={t('archive.reasonTip')} data-testid="hub-archive-reason" autoFocus />
        </label>
      </DangerZone>
    </div>
  );
}
